import { Search, X } from 'lucide-react';
import { ANY } from '../../../core/fleet-filters';
import type { SoldierFilters } from '../../../core/fleet-filters';
import { SOLDIER_ROLES, SOLDIER_ROLE_LABELS } from '../../../types/fleet';

interface SoldierFilterBarProps {
  filters: SoldierFilters;
  onChange: (next: SoldierFilters) => void;
  resultCount: number;
}

/**
 * Search + role filter for the roster. The box takes a full name, a personal
 * number, a phone, a role, a company or a platoon — the internal number is a
 * vehicle's and is searched for on the vehicles screen.
 */
export function SoldierFilterBar({ filters, onChange, resultCount }: SoldierFilterBarProps) {
  const set = <K extends keyof SoldierFilters>(key: K, value: SoldierFilters[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <div className="bg-panel border border-line rounded-2xl p-3 sm:p-4 space-y-3">
      <div className="relative">
        <Search
          size={18}
          className="absolute top-1/2 -translate-y-1/2 right-3 text-ink/40"
          aria-hidden
        />
        <input
          type="search"
          value={filters.text}
          onChange={(event) => set('text', event.target.value)}
          placeholder="חיפוש לפי שם, מספר אישי, טלפון, תפקיד, פלוגה או מחלקה"
          aria-label="חיפוש חייל"
          className="w-full h-11 rounded-xl border border-line bg-canvas pr-10 pl-10 text-sm focus:outline-none focus:border-brand-500 focus:bg-panel"
        />
        {filters.text ? (
          <button
            type="button"
            onClick={() => set('text', '')}
            title="ניקוי חיפוש"
            className="absolute top-1/2 -translate-y-1/2 left-2 w-7 h-7 rounded-lg flex items-center justify-center text-ink/40 hover:text-ink"
          >
            <X size={16} aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={filters.role}
          onChange={(event) => set('role', event.target.value as SoldierFilters['role'])}
          aria-label="סינון לפי תפקיד"
          className="h-10 rounded-xl border border-line bg-panel px-3 text-sm text-ink/80 focus:outline-none focus:border-brand-500"
        >
          <option value={ANY}>כל התפקידים</option>
          {SOLDIER_ROLES.map((role) => (
            <option key={role} value={role}>
              {SOLDIER_ROLE_LABELS[role]}
            </option>
          ))}
        </select>
      </div>

      <p className="text-xs text-ink/50 flex items-center gap-1.5">
        <span>{resultCount} חיילים מוצגים</span>
      </p>
    </div>
  );
}
